import React, { useState, useEffect, useCallback } from 'react';

// 영화거래소 admin_router의 CRAWLERS 키와 일치
const CRAWLERS = [
  { type: 'schedules', label: '상영시간표(7일)' },
  { type: 'boxoffice', label: '박스오피스·영화정보' },
];

const STATUS_KO = { running: '실행중', success: '성공', failed: '실패' };
const SITE_KO = { cinetree: '시네트리', exchange: '영화거래소' };

function fmtDuration(ms) {
  if (ms == null) return '-';
  const s = Math.round(ms / 1000);
  if (s < 60) return s + '초';
  return Math.floor(s / 60) + '분 ' + (s % 60) + '초';
}

export default function CrawlLogScreen() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [settings, setSettings] = useState([]);
  const [savingSite, setSavingSite] = useState(null);
  const [times, setTimes] = useState([]);          // 자동 크롤 실행시각 목록
  const [newHour, setNewHour] = useState('');
  const [newMinute, setNewMinute] = useState('');
  const [savingTime, setSavingTime] = useState(false);
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/request/getCrawlLogs', { credentials: 'include' });
      const data = await res.json();
      setLogs(Array.isArray(data) ? data : []);
    } catch (e) { console.error('로그 조회 실패:', e); }
    finally { setLoading(false); }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch('/request/getCrawlSettings', { credentials: 'include' });
      const data = await res.json();
      setSettings(Array.isArray(data) ? data : []);
    } catch (e) { console.error('설정 조회 실패:', e); }
  }, []);

  const fetchTimes = useCallback(async () => {
    try {
      const res = await fetch('/request/getCrawlTimes', { credentials: 'include' });
      const data = await res.json();
      setTimes(Array.isArray(data) ? data : []);
    } catch (e) { console.error('실행시각 조회 실패:', e); }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchSettings();
    fetchTimes();
    // 폴링 제거 → SSE: 크롤러가 시작/끝에 보내는 신호를 받을 때만 갱신
    let es;
    try {
      es = new EventSource('/request/crawlEvents', { withCredentials: true });
      es.onmessage = () => fetchLogs();
      es.onerror = () => { /* 브라우저가 자동 재연결(retry) */ };
    } catch (e) { console.error('SSE 연결 실패:', e); }
    return () => { if (es) es.close(); };
  }, [fetchLogs, fetchSettings, fetchTimes]);

  const updateField = (site, field, value) => {
    setSettings(prev => prev.map(s => (s.site === site ? { ...s, [field]: value } : s)));
  };

  const saveOne = async (s) => {
    setSavingSite(s.site);
    try {
      const body = { site: s.site, cutoff_hours: s.cutoff_hours, expose_days: s.expose_days };
      const res = await fetch('/request/saveCrawlSettings', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.ok) { alert((SITE_KO[s.site] || s.site) + ' 설정을 저장했습니다.'); fetchSettings(); }
      else alert('저장 실패: ' + (data.error || '알 수 없는 오류'));
    } catch (e) { alert('저장 요청 실패: ' + e.message); }
    finally { setSavingSite(null); }
  };

  // 실행시각 목록 CRUD — 변경 즉시 영화거래소 크론이 재등록됨
  const callTimeApi = async (path, body, okMsg) => {
    setSavingTime(true);
    try {
      const res = await fetch(path, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.ok) { if (okMsg) alert(okMsg); fetchTimes(); return true; }
      alert('실패: ' + (data.error || '알 수 없는 오류'));
      return false;
    } catch (e) { alert('요청 실패: ' + e.message); return false; }
    finally { setSavingTime(false); }
  };
  const addTime = async () => {
    const ok = await callTimeApi('/request/addCrawlTime', { hour: newHour, minute: newMinute }, '실행시각을 추가했습니다.');
    if (ok) { setNewHour(''); setNewMinute(''); }
  };
  const updateTime = (t) => callTimeApi('/request/updateCrawlTime', { id: t.id, hour: t.hour, minute: t.minute }, '실행시각을 수정했습니다.');
  const deleteTime = (t) => {
    if (!window.confirm(`매일 ${t.hour}시 ${t.minute}분 자동 크롤을 삭제할까요?`)) return;
    callTimeApi('/request/deleteCrawlTime', { id: t.id }, '실행시각을 삭제했습니다.');
  };
  const updateTimeField = (id, field, value) => {
    setTimes(prev => prev.map(t => (t.id === id ? { ...t, [field]: value } : t)));
  };

  const runCrawl = async (type, label) => {
    if (!window.confirm(`'${label}' 크롤링을 지금 실행할까요?`)) return;
    setRunning(true);
    try {
      const res = await fetch('/request/runCrawl', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type }),
      });
      const data = await res.json();
      if (data.ok) { alert(data.message || '크롤링을 시작했습니다.'); fetchLogs(); }
      else alert('실행 실패: ' + (data.error || '알 수 없는 오류'));
    } catch (e) { alert('실행 요청 실패: ' + e.message); }
    finally { setRunning(false); }
  };

  const cell = { textAlign: 'right' };

  return (
    <div style={{ padding: 20, width: '100%', overflow: 'auto' }}>
      <h2 style={{ marginTop: 0 }}>크롤링 설정 · 로그</h2>

      <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>예매 노출 설정 (사이트별)</h3>
        <table border="1" cellPadding="6" style={{ borderCollapse: 'collapse', fontSize: 13, margin: '0 auto', textAlign: 'center' }}>
          <thead style={{ background: '#f0f0f0' }}>
            <tr>
              <th>사이트</th>
              <th>컷오프<br/>(상영 N시간 전까지 예매)</th>
              <th>노출일수<br/>(오늘부터 며칠, 빈칸=무제한)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {settings.map(s => (
              <tr key={s.site}>
                <td>{SITE_KO[s.site] || s.site}</td>
                <td>
                  <input type="number" min="0" value={s.cutoff_hours != null ? s.cutoff_hours : ''}
                    onChange={e => updateField(s.site, 'cutoff_hours', e.target.value)} style={{ width: 70 }} /> 시간
                </td>
                <td>
                  <input type="number" min="1" value={s.expose_days != null ? s.expose_days : ''} placeholder="무제한"
                    onChange={e => updateField(s.site, 'expose_days', e.target.value)} style={{ width: 70 }} /> 일
                </td>
                <td>
                  <button disabled={savingSite === s.site} onClick={() => saveOne(s)} style={{ padding: '6px 14px' }}>
                    {savingSite === s.site ? '저장 중…' : '저장'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 style={{ marginBottom: 6 }}>자동 크롤 실행시각 (여러 개 등록 가능)</h3>
        <p style={{ color: '#666', fontSize: 12, marginTop: 0 }}>
          등록한 시각마다 박스오피스·영화정보 + 상영시간표 크롤이 자동 실행됩니다.
          (극장이 상영편성을 늦게 올리는 경우가 있어 새벽 + 낮 등 여러 번 돌리는 걸 권장)
        </p>
        {times.map(t => (
          <div key={t.id} style={{ marginBottom: 6 }}>
            매일{' '}
            <input type="number" min="0" max="23" value={t.hour}
              onChange={e => updateTimeField(t.id, 'hour', e.target.value)} style={{ width: 50 }} /> 시{' '}
            <input type="number" min="0" max="59" value={t.minute}
              onChange={e => updateTimeField(t.id, 'minute', e.target.value)} style={{ width: 50 }} /> 분{' '}
            <button disabled={savingTime} onClick={() => updateTime(t)} style={{ padding: '4px 12px', marginLeft: 8 }}>수정</button>
            <button disabled={savingTime} onClick={() => deleteTime(t)} style={{ padding: '4px 12px', marginLeft: 4, color: '#c00' }}>삭제</button>
          </div>
        ))}
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #ccc' }}>
          새 실행시각: {' '}
          <input type="number" min="0" max="23" value={newHour} placeholder="시"
            onChange={e => setNewHour(e.target.value)} style={{ width: 50 }} /> 시{' '}
          <input type="number" min="0" max="59" value={newMinute} placeholder="분"
            onChange={e => setNewMinute(e.target.value)} style={{ width: 50 }} /> 분{' '}
          <button disabled={savingTime} onClick={addTime} style={{ padding: '4px 12px', marginLeft: 8 }}>실행시각 추가</button>
        </div>
      </div>

      <p style={{ color: '#666', fontSize: 13 }}>
        자동 크롤링과 아래 수동 실행 기록이 표시됩니다. (크롤 시작·완료 시 자동 갱신)
      </p>
      <div style={{ marginBottom: 16 }}>
        {CRAWLERS.map(c => (
          <button key={c.type} disabled={running} onClick={() => runCrawl(c.type, c.label)}
            style={{ marginRight: 8, padding: '8px 16px', cursor: running ? 'wait' : 'pointer' }}>
            ▶ {c.label} 지금 실행
          </button>
        ))}
        <button onClick={fetchLogs} style={{ padding: '8px 16px', cursor: 'pointer' }}>새로고침</button>
        {loading && <span style={{ marginLeft: 8, color: '#888' }}>불러오는 중…</span>}
      </div>
      <table border="1" cellPadding="6" style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
        <thead style={{ background: '#f0f0f0' }}>
          <tr>
            <th>실행시각</th><th>종류</th><th>구분</th><th>소요</th><th>상태</th>
            <th>수집</th><th>신규</th><th>순위변동</th><th>포스터</th><th>갱신</th><th>중복/스킵</th><th>삭제</th><th>메시지</th>
          </tr>
        </thead>
        <tbody>
          {logs.length === 0 && (
            <tr><td colSpan="13" style={{ textAlign: 'center', color: '#999' }}>로그가 없습니다.</td></tr>
          )}
          {logs.map(l => (
            <tr key={l.id} style={{ background: l.status === 'failed' ? '#ffe5e5' : l.status === 'running' ? '#fff7e0' : '#fff' }}>
              <td style={{ whiteSpace: 'nowrap' }}>{l.start_time || '-'}</td>
              <td>{l.crawler_type}</td>
              <td>{l.triggered_by === 'cron' ? '자동' : '수동'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{fmtDuration(l.duration_ms)}</td>
              <td style={{ fontWeight: 'bold', color: l.status === 'failed' ? '#c00' : l.status === 'success' ? '#080' : '#a80' }}>
                {STATUS_KO[l.status] || l.status}
              </td>
              <td style={cell}>{l.collected_count != null ? l.collected_count : '-'}</td>
              <td style={cell}>{l.inserted_count != null ? l.inserted_count : '-'}</td>
              <td style={cell}>{l.rank_changed_count != null ? l.rank_changed_count : '-'}</td>
              <td style={cell}>{l.poster_count != null ? l.poster_count : '-'}</td>
              <td style={cell}>{l.updated_count != null ? l.updated_count : '-'}</td>
              <td style={cell}>{l.skipped_count != null ? l.skipped_count : '-'}</td>
              <td style={cell}>{l.deleted_count != null ? l.deleted_count : '-'}</td>
              <td style={{ maxWidth: 360, fontSize: 11, color: '#555', textAlign: 'center' }}>
                {l.message ? (
                  <div onClick={() => toggle(l.id)} title={expanded[l.id] ? '클릭하면 접기' : '클릭하면 펼치기'}
                    style={expanded[l.id]
                      ? { whiteSpace: 'pre-wrap', wordBreak: 'break-all', cursor: 'pointer', textAlign: 'center' }
                      : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', textAlign: 'center' }}>
                    {l.message}
                  </div>
                ) : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
